import React from 'react';
/**
 * Automatically calculates the element's max height, depending on it's position in the DOM
 *
 * The element, that will receive the returned ref, will calculate it's height depending on
 * the element's current top position in the DOM and will apply a max-height property according
 * to the space left over on the current page.
 *
 * In order to avoid forced reflows in CSS, we are using css variables - this way the document
 * does not need to be reflow just to get the element's current position while it is still rendering
 *
 * This is very useful when trying to implement scrollable tables that only span e.g. half a page,
 * whereas the top position may depend on certain elements being present (such as the institution picker)
 *
 * @example
 * ```
 * const elementRef = useDynamicHeight();
 * return (
 *   <div ref={elementRef}>
 *      {Some scrollable content}
 *   </div>
 * )
 * ```
 * @see {@link https://developers.google.com/speed/docs/insights/browser-reflow}
 * @author Domi <dr@famly.co>
 * @note the element, that will have the returned ref will have the properties `max-height` and `overflow` overwritten
 * @param fallbackHeight the height to apply to the element if the calculation does not work
 * @returns a Mutable ref to be attached to the element, whose max height is to be defined.
 */
export function useDynamicHeight({ fallbackHeight = '500px', baseMaxHeight = '97svh', disabled = false, } = { fallbackHeight: undefined, baseMaxHeight: undefined, disabled: undefined }) {
    const elementRef = React.useRef(null);
    React.useEffect(() => {
        const updateMaxHeight = () => {
            if (!disabled && elementRef.current) {
                const topPosition = elementRef.current.getBoundingClientRect().top;
                const maxHeight = `calc(${baseMaxHeight} - ${topPosition}px)`;
                elementRef.current.style.setProperty('--dynamic-max-height', maxHeight);
                elementRef.current.style.setProperty('max-height', `var(--dynamic-max-height, ${fallbackHeight})`);
                elementRef.current.style.setProperty('overflow', 'auto');
                elementRef.current.style.setProperty('padding-right', '8px');
            }
        };
        // Initial update
        updateMaxHeight();
        // Update on window resize
        window.addEventListener('resize', updateMaxHeight);
        // Cleanup
        return () => {
            window.removeEventListener('resize', updateMaxHeight);
        };
    }, [baseMaxHeight, disabled, fallbackHeight]);
    return elementRef;
}
